import { Role, type User } from "@cryptors/api-client";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { HttpError, IResourceComponentsProps } from "@refinedev/core";
import { Create } from "@refinedev/mui";
import { useForm } from "@refinedev/react-hook-form";
import { Controller } from "react-hook-form";

import { useGetAvatars } from "@/api/avatar.api";
import StyledSelect from "@/components/forms/StyledSelect";

import UserCryptorDraftAccessSelect from "./components/UserCryptorDraftAccessSelect";
import { userFormSchema, UserFormValues } from "./user-form";

export const UserCreate: React.FC<IResourceComponentsProps> = () => {
  const { data: avatars } = useGetAvatars();
  const {
    refineCore: { formLoading, onFinish },
    register,
    control,
    formState: { errors, isValid, isDirty },
    handleSubmit,
    watch,
  } = useForm<User, HttpError, UserFormValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      pseudo: "",
      email: "",
      password: "",
      emailConfirmed: false,
      role: Role.USER,
      avatarId: 1,
      cryptorsDraftAccess: [],
      isTesterAccount: false,
    },
  });

  const onFinishHandler = async (data: UserFormValues) => {
    await onFinish({
      pseudo: data.pseudo,
      email: data.email,
      password: data.password,
      emailConfirmed: data.emailConfirmed,
      role: data.role,
      avatarId: data.avatarId,
      cryptorsDraftAccess: data.cryptorsDraftAccess.map((entry) =>
        typeof entry === "number" ? entry : entry.id,
      ),
      isTesterAccount: data.isTesterAccount,
    });
  };

  return (
    <Create
      isLoading={formLoading}
      saveButtonProps={{
        onClick: handleSubmit(onFinishHandler),
        disabled: !isDirty || !isValid,
      }}
    >
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <TextField
          {...register("pseudo", {
            required: "Ce champ est requis",
          })}
          defaultValue={null}
          error={!!errors.pseudo}
          helperText={errors.pseudo?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Pseudo"
          name="pseudo"
        />
        <TextField
          {...register("email", {
            required: "Ce champ est requis",
          })}
          defaultValue={null}
          error={!!errors.email}
          helperText={errors.email?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="email"
          label="Email"
          name="email"
        />
        <Controller
          control={control}
          name="emailConfirmed"
          render={({ field }) => (
            <FormControlLabel
              label="Email confirmé"
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  onChange={(event) => {
                    field.onChange(event.target.checked);
                  }}
                />
              }
            />
          )}
        />
        <Controller
          control={control}
          name="isTesterAccount"
          render={({ field }) => (
            <FormControlLabel
              label="Est un testeur"
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  onChange={(event) => {
                    field.onChange(event.target.checked);
                  }}
                />
              }
            />
          )}
        />
        <TextField
          {...register("password", {
            required: "Ce champ est requis",
          })}
          defaultValue={null}
          error={!!errors.password}
          helperText={errors.password?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="password"
          label="Mot de passe"
          name="password"
        />
        <Controller
          control={control}
          name="role"
          render={({ field, fieldState }) => (
            <StyledSelect
              label="Role"
              onChange={field.onChange}
              onBlur={field.onBlur}
              value={field.value}
              error={fieldState.invalid}
            >
              <MenuItem value={Role.USER}>User</MenuItem>
              <MenuItem value={Role.ADMIN}>Admin</MenuItem>
            </StyledSelect>
          )}
        />
        <Controller
          control={control}
          name="avatarId"
          render={({ field, fieldState }) => (
            <StyledSelect
              label="Avatar"
              onChange={field.onChange}
              onBlur={field.onBlur}
              value={field.value}
              error={fieldState.invalid}
            >
              {avatars.map((avatar) => (
                <MenuItem key={avatar.id} value={avatar.id}>
                  <img alt="avatar" src={avatar.image.url} width={35} />
                </MenuItem>
              ))}
            </StyledSelect>
          )}
        />
        {watch("role") !== Role.ADMIN && (
          <UserCryptorDraftAccessSelect control={control} />
        )}
      </Box>
    </Create>
  );
};
